import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { API, useAuth } from '@/App';
import { SHARE_DOMAIN } from '@/lib/shareDomain';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, MapPin, Phone, Mail, Home, CalendarDays, IndianRupee, Users,
  Loader2, ArrowRight, Upload, FileText, Trash2, Download, Repeat, UserPlus, UserCheck,
  Heart, Plus, Sparkles, Image as ImageIcon, Save, Compass, Bed, User as UserIcon, Car,
  Eye, ChevronDown, ChevronUp,
} from 'lucide-react';
import { formatDate } from '@/lib/dateUtils';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import BulkActionBar from '@/components/BulkActionBar';
import AddTourFromInvoicesDialog from '@/components/AddTourFromInvoicesDialog';
import ContactSearchInput from '@/components/ContactSearchInput';
import RelationCombobox from '@/components/RelationCombobox';
import CustomerNetworkSection from '@/components/CustomerNetworkSection';
import { CareMomentsCard } from '@/components/CareMoments';
import DocumentViewer, { kindOf } from '@/components/DocumentViewer';
import ShareMapMenu from '@/components/ShareMapMenu';
import PollenDotsMap from '@/components/PollenDotsMap';
import { usePollenCapabilities } from '@/hooks/usePollenCapabilities';
import { useShowProfits } from '@/hooks/useShowProfits';
import PinButton from '@/components/PinButton';

const statusColors = { Planning: 'bg-gray-100 text-gray-800', Booked: 'bg-blue-100 text-blue-800', Ongoing: 'bg-amber-100 text-amber-800', Completed: 'bg-emerald-100 text-emerald-800' };

const PollenDotsCustomerProfile = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const caps = usePollenCapabilities();
  const [showProfits] = useShowProfits();
  const profitVisible = caps.is_super_admin && showProfits;
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteFileId, setDeleteFileId] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [familyExtractOpen, setFamilyExtractOpen] = useState(false);
  const [familyExtractText, setFamilyExtractText] = useState('');
  const [familyExtracting, setFamilyExtracting] = useState(false);
  const [savingFamily, setSavingFamily] = useState(false);
  // Active tab in the Journey section (Tours / Hotels / Locations / People).
  // Kept local to this page; resets on navigation.
  const [journeyTab, setJourneyTab] = useState('tours');

  const authFetch = (url, opts = {}) => {
    const h = {}; const t = localStorage.getItem('session_token'); if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await authFetch(`${POLLEN_API}/customers/${id}/profile`);
      if (res.ok) setProfile(await res.json());
      else { toast.error('Customer not found'); navigate('/pollen-dots/customers'); }
    } catch (e) { toast.error('Failed to load profile'); }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const [uploadCategory, setUploadCategory] = useState('Document');

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('customer_id', id);
    formData.append('category', uploadCategory || 'Document');
    try {
      const res = await authFetch(`${POLLEN_API}/files/upload`, { method: 'POST', body: formData });
      if (res.ok) { toast.success(`${uploadCategory} uploaded`); fetchProfile(); }
      else toast.error('Upload failed');
    } catch (err) { toast.error('Upload error'); }
    setUploading(false);
    e.target.value = '';
  };

  const setFileCategory = async (fileId, category) => {
    const res = await authFetch(`${POLLEN_API}/files/${fileId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category }),
    });
    if (res.ok) { toast.success(`Re-tagged as ${category}`); fetchProfile(); }
    else toast.error('Failed to re-categorize');
  };

  const handleDeleteFile = async (fileId) => {
    await authFetch(`${POLLEN_API}/files/${fileId}`, { method: 'DELETE' });
    toast.success('Document removed');
    setDeleteFileId(null);
    fetchProfile();
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await authFetch(`${POLLEN_API}/files/${fileId}/download`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { toast.error('Download failed'); }
  };

  // --- Family Members (Phase 4) ---
  const saveFamily = async (newFamily) => {
    setSavingFamily(true);
    try {
      const payload = {
        name: profile.name, phone: profile.phone || '', email: profile.email || '',
        address: profile.address || '', zoho_customer_id: profile.zoho_customer_id || '',
        referrer_id: profile.referrer_id || '', referrer_type: profile.referrer_type || 'direct',
        family_members: newFamily,
      };
      const res = await authFetch(`${POLLEN_API}/customers/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (res.ok) { setProfile(p => ({ ...p, family_members: newFamily })); toast.success('Family saved'); }
      else toast.error('Failed to save');
    } catch { toast.error('Error'); }
    setSavingFamily(false);
  };
  const addFamilyMember = () => {
    const next = [...(profile.family_members || []), { relation: 'Self', name: '', dob: '', anniversary: '', phone: '', notes: '' }];
    // Auto-expand the new row so the operator can start typing immediately
    // (the default-collapsed pattern saves screen real estate but would
    //  otherwise make every "Add" feel broken).
    setFamilyExpanded(s => ({ ...s, [next.length - 1]: true }));
    saveFamily(next);
  };
  // Local collapsed/expanded state for each family row — default
  // COLLAPSED so a customer with 8 family members doesn't drown the page.
  const [familyExpanded, setFamilyExpanded] = useState({});
  const toggleFamilyRow = (i) => setFamilyExpanded(s => ({ ...s, [i]: !s[i] }));

  const updateFamilyMember = (i, field, val) => {
    const next = (profile.family_members || []).map((m, idx) => idx === i ? { ...m, [field]: val } : m);
    setProfile(p => ({ ...p, family_members: next }));
  };
  const removeFamilyMember = (i) => saveFamily((profile.family_members || []).filter((_, idx) => idx !== i));

  const mergeExtractedFamily = (data) => {
    const newMembers = (data.family_members || []).filter(m => m.name);
    if (newMembers.length === 0) { toast.error('No family members detected'); return; }
    const merged = [...(profile.family_members || []), ...newMembers];
    saveFamily(merged);
    setFamilyExtractOpen(false);
    setFamilyExtractText('');
  };
  const handleFamilyExtractText = async () => {
    if (!familyExtractText.trim()) return;
    setFamilyExtracting(true);
    try {
      const res = await authFetch(`${POLLEN_API}/customers/${id}/extract-family-text`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: familyExtractText }),
      });
      if (res.ok) mergeExtractedFamily(await res.json()); else toast.error('Extraction failed');
    } catch { toast.error('Error'); }
    setFamilyExtracting(false);
  };
  const handleFamilyExtractFile = async (file) => {
    if (!file) return;
    setFamilyExtracting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await authFetch(`${POLLEN_API}/customers/${id}/extract-family`, { method: 'POST', body: fd });
      if (res.ok) mergeExtractedFamily(await res.json()); else { const e = await res.json(); toast.error(e.detail || 'Extraction failed'); }
    } catch { toast.error('Error'); }
    setFamilyExtracting(false);
  };

  if (!['Pollen Dots User','Super Admin','Admin','Operator','Intern'].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  if (loading) return <Layout><div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  if (!profile) return <Layout><div className="text-center py-20 text-muted-foreground">Customer not found</div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 min-w-0" data-testid="customer-profile-page">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 min-w-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Button variant="ghost" size="sm" className="shrink-0 -ml-2" onClick={() => navigate('/pollen-dots/customers')}><ArrowLeft className="w-4 h-4" /></Button>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-2xl font-bold tracking-tight truncate">{profile.name}</h1>
              <div className="flex gap-3 text-xs sm:text-sm text-muted-foreground mt-1 flex-wrap">
                {profile.phone && <span><Phone className="w-3 h-3 inline mr-1" />{profile.phone}</span>}
                {profile.email && <span><Mail className="w-3 h-3 inline mr-1" />{profile.email}</span>}
                {profile.address && <span className="truncate max-w-[14rem]"><Home className="w-3 h-3 inline mr-1" />{profile.address}</span>}
              </div>
            </div>
          </div>
          <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto sm:overflow-visible scrollbar-none">
            <div className="flex items-center gap-2 sm:flex-wrap sm:justify-end whitespace-nowrap">
              <PinButton entityType="customer" entityId={profile.id} />
              <ShareViewsChip views={profile.share_views} />
              <LoyaltyShareToggle
                customerId={profile.id}
                initialOn={!!profile.loyalty?.visible_in_share}
                onChange={(v) => setProfile(p => ({ ...p, loyalty: { ...(p.loyalty || {}), visible_in_share: v } }))}
              />
              <CustomerShareButton customerId={profile.id} customerName={profile.name} />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="customer-stats">
          <StatCard icon={IndianRupee} label="Lifetime Revenue" value={(profile.lifetime_revenue || 0).toLocaleString('en-IN')} color="text-amber-600" />
          <StatCard icon={MapPin} label="Trips" value={profile.trip_count || 0} color="text-blue-600" />
          <StatCard icon={CalendarDays} label="Days Travelled" value={profile.total_days || 0} color="text-purple-600" />
          <StatCard icon={CalendarDays} label="Last Travel" value={profile.last_travel_date ? formatDate(profile.last_travel_date) : 'N/A'} color="text-emerald-600" small />
          <StatCard icon={Repeat} label="Repeat Count" value={profile.repeat_count || 0} color="text-rose-600" />
        </div>

        {/* Referral metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="referral-stats">
          <StatCard icon={UserCheck} label="Referred By" value={profile.referrer?.name || 'Direct'} sub={profile.referrer?.type === 'team_member' ? 'Team Member' : profile.referrer?.type === 'customer' ? 'Customer' : profile.referrer?.type === 'personal_contact' ? 'Personal Contact' : ''} color="text-indigo-600" small />
          <StatCard icon={UserPlus} label="Customers Referred" value={(profile.referred_customers || []).length} color="text-teal-600" />
          <StatCard icon={IndianRupee} label="Referral Revenue" value={`₹${(profile.referral_revenue || 0).toLocaleString('en-IN')}`} color="text-emerald-600" />
          {profitVisible && profile.referral_profit != null && (
            <StatCard icon={IndianRupee} label="Referral Profit" value={`₹${(profile.referral_profit || 0).toLocaleString('en-IN')}`} sub="Post FY 2026-27 only" color={(profile.referral_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'} />
          )}
          {/* Loyalty Credits — 1 credit per ₹1,000 referral revenue · 1 credit = ₹10 */}
          <div data-testid="loyalty-card">
            <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-amber-100 text-amber-700"><Sparkles className="w-3.5 h-3.5" /></div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-amber-800/80 leading-tight">Loyalty Credits</p>
                    <p className="text-lg font-bold text-amber-900 tabular-nums">{profile.loyalty?.credits ?? 0}</p>
                    <p className="text-[10px] text-amber-700">≈ ₹{(profile.loyalty?.rupee_value || 0).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Referred Businesses — detailed per-customer breakdown with trip list */}
        {(profile.referred_customers || []).length > 0 && (
          <Card data-testid="referred-businesses-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-teal-600" />
                Referred Businesses by {profile.name}
                <Badge variant="outline" className="text-[10px] ml-1">{profile.referred_customers.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {profile.referred_customers.map((c) => (
                  <ReferredCustomerRow key={c.id} c={c} navigate={navigate}
                    profitVisible={profitVisible} statusColors={statusColors} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Family & Important Dates (Phase 4) */}
        <Card data-testid="family-section">
          <CardHeader className="pb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2"><Heart className="w-4 h-4 text-rose-600" />Family & Important Dates ({(profile.family_members || []).length})</CardTitle>
            {/* Buttons collapse to icon-only on mobile so they always fit
                inside the card width, even with a long row count. */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              <Button variant="outline" size="sm" onClick={() => setFamilyExtractOpen(true)} data-testid="family-extract-btn" className="h-8 px-2" title="AI Extract from passport / booking form"><Sparkles className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">AI Extract</span></Button>
              <Button variant="outline" size="sm" onClick={addFamilyMember} disabled={savingFamily} data-testid="add-family-btn" className="h-8 px-2" title="Add family member"><Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Add</span></Button>
            </div>
          </CardHeader>
          <CardContent>
            {(profile.family_members || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No family members added. Use AI Extract from a passport / booking form, or add manually.</p>
            ) : (
              <div className="space-y-2">
                {profile.family_members.map((m, i) => {
                  // One-line summary visible when the row is collapsed —
                  // surfaces 🎂 DOB / 💍 Anniversary / phone so the
                  // operator can scan a customer's whole household
                  // without expanding every row.
                  const summaryBits = [
                    m.dob && `🎂 ${m.dob.slice(0, 10)}`,
                    m.anniversary && `💍 ${m.anniversary.slice(0, 10)}`,
                    m.phone,
                  ].filter(Boolean);
                  return (
                  <div key={i} className="border rounded-lg relative" data-testid={`family-row-${i}`}>
                    <div className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                         onClick={() => toggleFamilyRow(i)}
                         data-testid={`family-row-${i}-toggle`}>
                      {familyExpanded[i]
                        ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-none" />
                        : <ChevronDown className="w-4 h-4 text-muted-foreground flex-none" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">
                            {m.name || <span className="italic text-muted-foreground">Untitled</span>}
                          </span>
                          <Badge variant="secondary" className="text-[10px] shrink-0">
                            {m.relation || 'Self'}
                          </Badge>
                        </div>
                        {summaryBits.length > 0 && (
                          <div className="text-[11px] text-muted-foreground truncate">
                            {summaryBits.join(' · ')}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-1" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" title="Convert to customer"
                                className="h-6 w-6 p-0 text-emerald-700 hover:bg-emerald-50"
                                data-testid={`family-convert-btn-${i}`}
                                onClick={async () => {
                                  if (!m.name || !m.name.trim()) { toast.error('Add a name first'); return; }
                                  if (!window.confirm(`Create a new customer record from "${m.name}"?`)) return;
                                  try {
                                    const t = localStorage.getItem('session_token');
                                    const r = await fetch(`${API}/pollen/customers/from-family`, {
                                      method: 'POST', credentials: 'include',
                                      headers: { 'Content-Type': 'application/json',
                                                 ...(t ? { Authorization: `Bearer ${t}` } : {}) },
                                      body: JSON.stringify({
                                        name: m.name, phone: m.phone || '',
                                        dob: m.dob || '', anniversary: m.anniversary || '',
                                        notes: m.notes || '', referred_by_id: profile.id,
                                      }),
                                    });
                                    if (!r.ok) throw new Error('Could not create customer');
                                    const out = await r.json();
                                    toast.success(`Customer "${out.name}" created — linked as referred by ${profile.name}`);
                                  } catch (e) { toast.error(e.message); }
                                }}>
                          <Plus className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive"
                                onClick={() => removeFamilyMember(i)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    {familyExpanded[i] && (
                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 p-3 pt-0 border-t">
                    <div><Label className="text-xs">Relation</Label>
                      <RelationCombobox
                        value={m.relation || 'Self'}
                        onChange={v => updateFamilyMember(i, 'relation', v)}
                        onBlur={() => saveFamily(profile.family_members)}
                        testId={`family-relation-${i}`}
                      />                    </div>
                    <div className="sm:col-span-2 pr-6">
                      <Label className="text-xs">Name</Label>
                      <ContactSearchInput
                        value={m.name || ''}
                        onChange={(v) => updateFamilyMember(i, 'name', v)}
                        onSelect={(rec) => {
                          // Pre-fill every other field from the chosen
                          // contact so the operator doesn't have to type
                          // it twice. Existing typed values win — we
                          // only fill what's currently blank.
                          const next = (profile.family_members || []).map((mm, idx) => {
                            if (idx !== i) return mm;
                            return {
                              ...mm,
                              name: rec.name || mm.name || '',
                              dob: mm.dob || (rec.dob || '').slice(0, 10),
                              anniversary: mm.anniversary || (rec.anniversary || '').slice(0, 10),
                              phone: mm.phone || rec.phone || '',
                              relation: mm.relation && mm.relation !== 'Self'
                                ? mm.relation : (rec.relation || mm.relation || 'Self'),
                              notes: mm.notes || rec.notes || '',
                            };
                          });
                          setProfile(p => ({ ...p, family_members: next }));
                          saveFamily(next);
                          toast.success(`Pre-filled from ${rec.via || 'existing contact'}`);
                        }}
                        placeholder="Search customers, family, co-travellers"
                        testId={`family-name-search-${i}`}
                      />
                    </div>
                    <div><Label className="text-xs">DOB</Label><Input type="date" className="h-8 text-sm" value={m.dob || ''} onChange={e => updateFamilyMember(i, 'dob', e.target.value)} onBlur={() => saveFamily(profile.family_members)} /></div>
                    <div><Label className="text-xs">Anniversary</Label><Input type="date" className="h-8 text-sm" value={m.anniversary || ''} onChange={e => updateFamilyMember(i, 'anniversary', e.target.value)} onBlur={() => saveFamily(profile.family_members)} /></div>
                    <div><Label className="text-xs">Phone (for wishes)</Label><Input type="tel" className="h-8 text-sm" placeholder="+91 or local" value={m.phone || ''} onChange={e => updateFamilyMember(i, 'phone', e.target.value)} onBlur={() => saveFamily(profile.family_members)} /></div>
                    <div><Label className="text-xs">Notes</Label><Input className="h-8 text-sm" value={m.notes || ''} onChange={e => updateFamilyMember(i, 'notes', e.target.value)} onBlur={() => saveFamily(profile.family_members)} placeholder="Allergies / prefs" /></div>
                    </div>
                    )}
                  </div>
                );})}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Journey — 4 creative tabs in a 2x2 bento grid on mobile, horizontal on desktop */}
        <JourneySection
          tab={journeyTab}
          setTab={setJourneyTab}
          profile={profile}
          navigate={navigate}
          statusColors={statusColors}
          caps={caps}
          onReload={fetchProfile}
        />

        {/* Network — Family Tree · Friend List · Referral Tree */}
        <CustomerNetworkSection customerId={profile.id} customerName={profile.name} />

        {/* Dots of Care — every touchpoint delivered for this traveller */}
        <CareMomentsCard customerId={profile.id} customerName={profile.name} />

        {/* Travel Companions — aggregated from every tour's co-travellers */}
        {(profile.travel_companions || []).length > 0 && (
          <Card data-testid="companions-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-indigo-600" />Travel Companions ({profile.travel_companions.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {profile.travel_companions.map((c, i) => (
                  <div key={i} className="p-3 rounded-lg border bg-muted/20" data-testid={`companion-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      {c.customer_id ? (
                        <button onClick={() => navigate(`/pollen-dots/customer/${c.customer_id}`)} className="font-medium text-sm text-primary hover:underline text-left" data-testid={`companion-link-${i}`}>
                          {c.name}
                        </button>
                      ) : (
                        <p className="font-medium text-sm">{c.name}</p>
                      )}
                      <Badge variant="outline" className="text-[10px] shrink-0"><Repeat className="w-3 h-3 mr-1" />{c.trip_count} trip{c.trip_count > 1 ? 's' : ''}</Badge>
                    </div>
                    {c.customer_id && (
                      <p className="text-[10px] text-emerald-700 mt-0.5">Also a Pollen Dots customer — tap name to open profile</p>
                    )}
                    {c.notes && <p className="text-xs text-muted-foreground mt-1">{c.notes}</p>}
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {c.trips.slice(0, 3).map((tr, j) => (
                        <button key={j} onClick={() => navigate(`/pollen-dots/tour/${tr.tour_id}`)}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-white border hover:bg-primary/5 text-muted-foreground">
                          {tr.destination || tr.tour_name || 'Trip'} · {tr.start_date?.slice(0, 7) || ''}
                        </button>
                      ))}
                      {c.trips.length > 3 && <span className="text-[10px] text-muted-foreground self-center">+{c.trips.length - 3}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document Vault — grouped by category (Invoice / Itinerary / Document) with inline preview */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">Document Vault</CardTitle>
              <div className="flex items-center gap-2">
                <Select value={uploadCategory} onValueChange={setUploadCategory}>
                  <SelectTrigger className="h-8 text-xs w-[130px]" data-testid="cust-upload-cat-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Invoice">Invoice</SelectItem>
                    <SelectItem value="Itinerary">Itinerary PDF</SelectItem>
                    <SelectItem value="Document">Document</SelectItem>
                  </SelectContent>
                </Select>
                <label className="cursor-pointer">
                  <input type="file" className="hidden" onChange={handleFileUpload} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv" data-testid="doc-upload-input" />
                  <Button variant="outline" size="sm" asChild disabled={uploading}>
                    <span>{uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Upload</span>
                  </Button>
                </label>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(profile.documents || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No documents uploaded. Pick a category and click Upload.</p>
            ) : (() => {
              const groups = { Invoice: [], Itinerary: [], Document: [] };
              for (const f of profile.documents) {
                const c = groups[f.category] ? f.category : 'Document';
                groups[c].push(f);
              }
              const order = ['Invoice', 'Itinerary', 'Document'];
              const labels = { Invoice: 'Invoices', Itinerary: 'Itinerary PDFs', Document: 'Documents' };
              const tints = {
                Invoice: 'bg-amber-50 text-amber-700 border-amber-200',
                Itinerary: 'bg-sky-50 text-sky-700 border-sky-200',
                Document: 'bg-muted text-foreground border-border',
              };
              const kindTint = {
                pdf: 'bg-rose-50 text-rose-700 border-rose-200',
                image: 'bg-teal-50 text-teal-700 border-teal-200',
                sheet: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                docx: 'bg-sky-50 text-sky-700 border-sky-200',
                other: 'bg-muted text-foreground border-border',
              };
              return (
                <div className="space-y-5">
                  {order.map(cat => groups[cat].length > 0 && (
                    <div key={cat} data-testid={`cust-docs-group-${cat.toLowerCase()}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className={`${tints[cat]} text-[10px]`}>{labels[cat]}</Badge>
                        <span className="text-xs text-muted-foreground">{groups[cat].length}</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {groups[cat].map((doc, i) => {
                          const kind = kindOf(doc);
                          const ext = (doc.original_filename || '').split('.').pop().toUpperCase();
                          return (
                            <div key={doc.id || i} className="group relative border rounded-lg bg-white hover:shadow-md transition-all overflow-hidden" data-testid={`cust-doc-card-${doc.id}`}>
                              <button type="button" onClick={() => setPreviewFile(doc)} className="w-full text-left" data-testid={`cust-doc-preview-${doc.id}`}>
                                <div className={`aspect-[3/4] flex flex-col items-center justify-center gap-2 border-b ${kindTint[kind] || kindTint.other}`}>
                                  <FileText className="w-10 h-10 opacity-70" />
                                  <span className="text-[10px] font-mono tracking-wider">{ext}</span>
                                  <span className="text-[10px] opacity-70">Click to preview</span>
                                </div>
                                <div className="p-2">
                                  <p className="text-xs font-medium truncate" title={(doc.display_name && doc.display_name.trim()) || doc.original_filename}>{(doc.display_name && doc.display_name.trim()) || doc.original_filename}</p>
                                  <p className="text-[10px] text-muted-foreground">
                                    {doc.size != null ? `${(doc.size / 1024).toFixed(1)} KB` : ''}
                                    {doc.created_at ? ` · ${doc.created_at.slice(0, 10)}` : ''}
                                  </p>
                                  {doc.tour_name && <p className="text-[10px] text-primary truncate">{doc.tour_name}</p>}
                                </div>
                              </button>
                              <div className="absolute top-1 right-1 flex gap-1">
                                <Select value={cat} onValueChange={(v) => setFileCategory(doc.id, v)}>
                                  <SelectTrigger className="h-6 text-[10px] px-1.5 py-0 w-[85px] bg-white/90" onClick={(e) => e.stopPropagation()}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Invoice">Invoice</SelectItem>
                                    <SelectItem value="Itinerary">Itinerary</SelectItem>
                                    <SelectItem value="Document">Document</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button variant="secondary" size="sm" className="h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); downloadFile(doc.id, doc.original_filename); }} title="Download">
                                  <Download className="w-3 h-3" />
                                </Button>
                                <Button variant="secondary" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteFileId(doc.id); }} title="Delete">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </CardContent>
        </Card>
        <DocumentViewer
          open={!!previewFile}
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          onDownload={(f) => downloadFile(f.id, f.original_filename)}
          onRenamed={() => fetchProfile()}
        />
        <ConfirmDeleteDialog open={!!deleteFileId} onClose={() => setDeleteFileId(null)} onConfirm={() => handleDeleteFile(deleteFileId)} title="Delete Document" description="This will remove this document." />

        {/* Family AI Extract Dialog */}
        <Dialog open={familyExtractOpen} onOpenChange={setFamilyExtractOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-600" />AI Extract Family Members</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Upload passport scan / booking form / pax list (image or PDF), or paste text. AI will extract names, DOB, anniversaries, notes.</p>
              <div className="flex gap-2 flex-wrap">
                <label className="flex items-center gap-1 cursor-pointer border rounded-md px-3 py-1.5 text-sm hover:bg-muted">
                  <ImageIcon className="w-4 h-4" /> Image
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleFamilyExtractFile(e.target.files?.[0])} />
                </label>
                <label className="flex items-center gap-1 cursor-pointer border rounded-md px-3 py-1.5 text-sm hover:bg-muted">
                  <FileText className="w-4 h-4" /> PDF / DOCX / XLSX
                  <input type="file" accept=".pdf,.doc,.docx,.xlsx,.xls,.txt" className="hidden" onChange={e => handleFamilyExtractFile(e.target.files?.[0])} />
                </label>
              </div>
              <div>
                <Label>Or paste text</Label>
                <Textarea rows={6} value={familyExtractText} onChange={e => setFamilyExtractText(e.target.value)} placeholder="Paste pax list / email / booking form..." data-testid="family-extract-text" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setFamilyExtractOpen(false)}>Close</Button>
                <Button onClick={handleFamilyExtractText} disabled={familyExtracting || !familyExtractText.trim()}>
                  {familyExtracting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}Extract from Text
                </Button>
              </div>
              {familyExtracting && <p className="text-xs text-muted-foreground text-center">Extracting... may take 10-20 seconds</p>}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const StatCard = ({ icon: Icon, label, value, color, small, sub }) => (
  <Card><CardContent className="pt-4 pb-3 px-4">
    <div className="flex items-center gap-2">
      <div className={`p-1.5 rounded-lg bg-muted ${color}`}><Icon className="w-3.5 h-3.5" /></div>
      <div>
        <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
        <p className={`${small ? 'text-xs' : 'text-lg'} font-bold`}>{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
      </div>
    </div>
  </CardContent></Card>
);

// ---- Referred Customer row with expandable tour breakdown ----
// Clicking the chevron opens the list of tours that referred customer has taken with us.
const ReferredCustomerRow = ({ c, navigate, profitVisible, statusColors }) => {
  const [open, setOpen] = useState(false);
  const rev = c.lifetime_revenue || 0;
  const profit = c.gross_profit || 0;
  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`referred-${c.id}`}>
      <div className="p-3 flex items-center justify-between gap-2 hover:bg-muted/30">
        <button type="button" onClick={() => navigate(`/pollen-dots/customer/${c.id}`)}
          className="flex-1 text-left min-w-0">
          <p className="font-medium text-sm truncate">{c.name}</p>
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-0.5">
            {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
            <span>{c.trip_count || 0} trip{(c.trip_count || 0) !== 1 ? 's' : ''}</span>
          </div>
        </button>
        <div className="text-right shrink-0">
          <p className="font-bold text-sm">₹{rev.toLocaleString('en-IN')}</p>
          {profitVisible && profit !== 0 && (
            <p className={`text-xs ${profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              Profit: ₹{profit.toLocaleString('en-IN')}
            </p>
          )}
        </div>
        {(c.tours || []).length > 0 && (
          <button onClick={() => setOpen(!open)} className="p-1 rounded hover:bg-muted"
            data-testid={`expand-referred-${c.id}`}>
            <ArrowRight className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
          </button>
        )}
      </div>
      {open && (c.tours || []).length > 0 && (
        <div className="bg-muted/20 px-3 py-2 space-y-1.5 border-t">
          {c.tours.map((t) => (
            <div key={t.id}
              className="flex items-center justify-between gap-2 p-2 rounded bg-white border cursor-pointer hover:border-primary/30"
              onClick={() => navigate(`/pollen-dots/tour/${t.id}`)}
              data-testid={`referred-tour-${t.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate">{t.tour_name || 'Untitled'}</span>
                  {t.status && <Badge className={`text-[10px] ${statusColors[t.status] || 'bg-gray-100'}`} variant="outline">{t.status}</Badge>}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {t.destination || '—'}{t.destination && t.start_date && ' · '}
                  {t.start_date && `${formatDate(t.start_date)} → ${formatDate(t.end_date)}`}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-semibold">₹{(t.customer_amount || 0).toLocaleString('en-IN')}</p>
                {profitVisible && t.gross_profit != null && (
                  <p className={`text-[10px] ${(t.gross_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    P: ₹{(t.gross_profit || 0).toLocaleString('en-IN')}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ---- ShareViewsChip ----
// Operator-only view counter. Reads `share_views: { total, last_30d, last_7d,
// last_viewed_at }` from the profile endpoint. NEVER rendered inside the
// public share page (the public endpoint doesn't expose these fields — see
// server.py). Hidden when there are zero views so fresh customer profiles
// stay uncluttered.
const ShareViewsChip = ({ views }) => {
  const total = Number(views?.total || 0);
  // Always render (even at 0 views) so the operator can see the counter is
  // live and gets a mental anchor for "how much pickup this share is getting".
  const last30 = Number(views?.last_30d || 0);
  const last7 = Number(views?.last_7d || 0);
  const lastAt = views?.last_viewed_at;
  const formatAgo = (iso) => {
    if (!iso) return '';
    try {
      const diffMs = Date.now() - new Date(iso).getTime();
      const mins = Math.floor(diffMs / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      if (days < 30) return `${days}d ago`;
      return `${Math.floor(days / 30)}mo ago`;
    } catch { return ''; }
  };
  const tooltip = total === 0
    ? 'Share link has never been opened yet. Views are tracked privately — customers cannot see this count.'
    : [
        `${total} total view${total === 1 ? '' : 's'}`,
        `${last30} in last 30d`,
        `${last7} in last 7d`,
        lastAt ? `Last opened ${formatAgo(lastAt)}` : '',
        '(Private — customers cannot see this.)',
      ].filter(Boolean).join(' · ');
  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50/80 text-slate-700"
      data-testid="share-views-chip"
      title={tooltip}
    >
      <Eye className="w-3.5 h-3.5 text-slate-500 shrink-0" />
      <span className="text-xs font-medium tabular-nums leading-none whitespace-nowrap">
        {last30}
        <span className="text-slate-400 font-normal"> view{last30 === 1 ? '' : 's'} · 30d</span>
      </span>
    </div>
  );
};


// ---- LoyaltyShareToggle ----
// Small inline switch shown next to the Share button on the customer header.
// Controls whether the public share link exposes Loyalty Credits. Defaults OFF
// (loyalty stays internal until the operator flips it on per-customer).
const LoyaltyShareToggle = ({ customerId, initialOn, onChange }) => {
  const [on, setOn] = useState(!!initialOn);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setOn(!!initialOn); }, [initialOn]);
  const toggle = async (next) => {
    setSaving(true);
    setOn(next); // optimistic
    try {
      const t = localStorage.getItem('session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (t) headers['Authorization'] = `Bearer ${t}`;
      const res = await fetch(`${API}/pollen/customers/${customerId}/share-options`, {
        method: 'PATCH',
        credentials: 'include',
        headers,
        body: JSON.stringify({ loyalty_visible_in_share: next }),
      });
      if (!res.ok) throw new Error('Update failed');
      onChange?.(next);
      toast.success(next ? 'Loyalty visible on share link' : 'Loyalty hidden on share link');
    } catch (e) {
      setOn(!next); // revert
      toast.error(e.message || 'Could not update');
    } finally {
      setSaving(false);
    }
  };
  return (
    <label
      className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-2.5 py-1.5 rounded-md border border-amber-200 bg-amber-50/60 cursor-pointer hover:bg-amber-50 transition-colors"
      data-testid="loyalty-share-toggle"
      title="Show Loyalty Credits on this customer's public share link"
    >
      <Sparkles className="w-3.5 h-3.5 text-amber-600 shrink-0" />
      <span className="hidden sm:inline text-xs text-amber-900 leading-none">Show loyalty</span>
      <Switch
        checked={on}
        onCheckedChange={toggle}
        disabled={saving}
        data-testid="loyalty-share-switch"
        className="data-[state=checked]:bg-amber-500"
      />
    </label>
  );
};


// ---- CustomerShareButton ----
// Generates (or reuses) a public share token and exposes a "Share" menu
// (WhatsApp / Copy link / Download PNG) via ShareMapMenu.
const CustomerShareButton = ({ customerId, customerName }) => {
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const ensureShareUrl = useCallback(async () => {
    if (shareUrl) return shareUrl;
    setLoading(true);
    try {
      const t = localStorage.getItem('session_token');
      const headers = {};
      if (t) headers['Authorization'] = `Bearer ${t}`;
      const res = await fetch(`${API}/pollen/customers/${customerId}/share-token`, {
        method: 'POST',
        credentials: 'include',   // include cookie session (primary auth)
        headers,
      });
      if (!res.ok) {
        let detail = 'Could not create share link';
        try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch (e) { /* ignore */ }
        throw new Error(detail);
      }
      const data = await res.json();
      // Include a timestamp-based cache-buster so WhatsApp/Twitter re-fetch
      // the OG preview whenever we re-generate the share URL — avoids stale
      // previews after a customer edits their name or locations.
      const v = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      // Pretty share URL on the operator's own domain. Falls back to the
      // current origin during local/preview testing. ArtWorksIT's Nginx
      // proxies pollendots.com/* through to the Emergent app, where the
      // /connection/:token React route renders the share page directly.
      // The static branded OG card in index.html keeps WhatsApp previews
      // looking on-brand even though the URL is no longer the OG endpoint.
      const SHARE_ORIGIN = SHARE_DOMAIN;
      const url = `${SHARE_ORIGIN}/connection/${data.share_token}?v=${v}`;
      setShareUrl(url);
      return url;
    } catch (e) {
      toast.error(e.message);
      return '';
    } finally {
      setLoading(false);
    }
  }, [customerId, shareUrl]);
  // Eagerly generate on mount so the Share dropdown items work instantly.
  useEffect(() => { ensureShareUrl(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [customerId]);

  return (
    <ShareMapMenu
      shareUrl={shareUrl}
      whatsappText={`Look at all the places we've travelled together, ${customerName}! Here's your Pollen Dots footprint with us 🌸`}
      filename={`${(customerName || 'customer').toLowerCase().replace(/\s+/g, '-')}-pollen-dots`}
    />
  );
};


// ---- Customer Journey tabs (Tours / Hotels / Places / People) ----
// Creative 2x2 bento grid on mobile → horizontal row on desktop. Each chip is a soft
// pastel card with its own accent color so the customer's journey feels curated rather
// than just a list of database rows.
const JOURNEY_TABS = [
  { key: 'tours',  label: 'Tours',  icon: Compass,  accent: 'orange', tint: 'from-orange-50 to-amber-50',  border: 'border-orange-200',  iconTint: 'text-orange-600',  ring: 'ring-orange-400/40' },
  { key: 'hotels', label: 'Hotels', icon: Bed,      accent: 'sky',    tint: 'from-sky-50 to-blue-50',      border: 'border-sky-200',     iconTint: 'text-sky-600',      ring: 'ring-sky-400/40' },
  { key: 'places', label: 'Places', icon: MapPin,   accent: 'teal',   tint: 'from-teal-50 to-emerald-50',  border: 'border-teal-200',    iconTint: 'text-teal-600',     ring: 'ring-teal-400/40' },
  { key: 'people', label: 'People', icon: UserIcon, accent: 'rose',   tint: 'from-rose-50 to-pink-50',     border: 'border-rose-200',    iconTint: 'text-rose-600',     ring: 'ring-rose-400/40' },
];

const JourneySection = ({ tab, setTab, profile, navigate, statusColors, caps, onReload }) => {
  const [addTourOpen, setAddTourOpen] = useState(false);
  const counts = {
    tours: profile.tours?.length || 0,
    hotels: profile.hotels_stayed?.length || 0,
    places: profile.locations_visited?.length || 0,
    people: profile.people_met?.length || 0,
  };

  // Fixed map pins: places + enroute stops + hotels + guide/driver base locations (if any have coords).
  const places = profile.locations_visited || [];
  const hotels = profile.hotels_stayed || [];
  const people = profile.people_met || [];
  const placePins = places.filter(p => p.lat != null && p.lng != null && p.kind !== 'enroute_stop')
    .map(p => ({ type: 'place', name: p.name, lat: p.lat, lng: p.lng, verified: !!p.verified, trip_count: p.trip_count, trips: p.trips }));
  const stopPins = places.filter(p => p.lat != null && p.lng != null && p.kind === 'enroute_stop')
    .map(p => ({ type: 'stop', name: p.name, subtype: p.subtype || 'Other', subtypes: p.subtypes || [], notes: p.notes || '', google_maps_url: p.google_maps_url || '', lat: p.lat, lng: p.lng, verified: !!p.verified, trip_count: p.trip_count, trips: p.trips }));
  const hotelPins = hotels.filter(h => h.lat != null && h.lng != null)
    .map(h => ({ type: 'hotel', name: h.hotel_name, location: h.location, google_maps_url: h.google_maps_url || '', lat: h.lat, lng: h.lng, verified: !!h.verified, trip_count: h.trip_count, trips: h.trips }));
  const guidePins = people.filter(p => p.role === 'Guide' && p.lat != null && p.lng != null)
    .map(p => ({ type: 'guide', name: p.name, location: p.location, lat: p.lat, lng: p.lng, verified: !!p.verified, trip_count: p.trip_count, trips: p.trips, vendor_id: p.vendor_id }));
  const driverPins = people.filter(p => p.role === 'Driver' && p.lat != null && p.lng != null)
    .map(p => ({ type: 'driver', name: p.name, location: p.location, lat: p.lat, lng: p.lng, verified: !!p.verified, trip_count: p.trip_count, trips: p.trips, vendor_id: p.vendor_id }));
  const mapPins = [...placePins, ...stopPins, ...hotelPins, ...guidePins, ...driverPins];
  const peopleUnmapped = people.length - guidePins.length - driverPins.length;
  const unmappedCount = (places.length - placePins.length) + (hotels.length - hotelPins.length) + Math.max(0, peopleUnmapped);

  return (
    <Card data-testid="journey-section">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Compass className="w-4 h-4 text-primary" />Their Journey
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAddTourOpen(true)}
          data-testid="customer-add-tour-btn"
          title="Create a tour for this customer — supports multiple invoices rolled into one"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Tour
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tab chips — responsive 2x2 → 4x1 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3" data-testid="journey-tabs">
          {JOURNEY_TABS.map(t => {
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                className={`text-left p-3 rounded-xl border transition-all ${
                  active
                    ? `bg-gradient-to-br ${t.tint} ${t.border} shadow-sm ring-2 ${t.ring}`
                    : 'bg-white border-border hover:border-foreground/20 hover:shadow-sm'
                }`}
                data-testid={`journey-tab-${t.key}`}>
                <div className="flex items-center justify-between">
                  <Icon className={`w-5 h-5 ${active ? t.iconTint : 'text-muted-foreground'}`} />
                  <span className={`text-2xl font-bold tabular-nums ${active ? t.iconTint : 'text-foreground'}`}>{counts[t.key]}</span>
                </div>
                <p className={`text-xs font-medium mt-1.5 ${active ? t.iconTint : 'text-muted-foreground'}`}>{t.label}</p>
              </button>
            );
          })}
        </div>

        {/* Fixed map — always visible under the 4 counters (places + hotels + guide/driver bases). */}
        {mapPins.length > 0 ? (
          <div className="space-y-2" data-testid="journey-map-wrap">
            <JourneyMap pins={mapPins} onPinClick={(pin) => {
              // Hotels & enroute stops show a rich popup (name, address,
              // Open Map link) — clicking the pin should leave the popup
              // open, not navigate away from the map.
              if (pin.type === 'hotel' || pin.type === 'stop') return;
              if (pin.type === 'guide' || pin.type === 'driver') {
                if (pin.vendor_id) navigate(`/pollen-dots/vendor/${pin.vendor_id}`);
              } else if (pin.trips?.[0]) {
                navigate(`/pollen-dots/tour/${pin.trips[0].tour_id}`);
              }
            }} />
            <div className="flex items-center flex-wrap gap-3 text-[11px] px-1" data-testid="journey-map-legend">
              <span className="flex items-center gap-1.5 text-teal-700">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-teal-600 ring-2 ring-teal-600/20" />
                Places <span className="text-muted-foreground">· {placePins.length}</span>
              </span>
              <span className="flex items-center gap-1.5 text-blue-700">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-600 ring-2 ring-blue-600/20" />
                Hotels <span className="text-muted-foreground">· {hotelPins.length}</span>
              </span>
              <span className="flex items-center gap-1.5 text-rose-700">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-600 ring-2 ring-rose-600/20" />
                Guide base <span className="text-muted-foreground">· {guidePins.length}</span>
              </span>
              <span className="flex items-center gap-1.5 text-yellow-700">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500 ring-2 ring-yellow-500/20" />
                Driver base <span className="text-muted-foreground">· {driverPins.length}</span>
              </span>
              {unmappedCount > 0 && <span className="text-muted-foreground">({unmappedCount} not yet mapped)</span>}
            </div>
          </div>
        ) : (places.length + hotels.length + people.length) > 0 ? (
          <div className="py-5 text-center text-xs text-muted-foreground border rounded-lg bg-muted/20 space-y-2" data-testid="journey-map-empty">
            <MapPin className="w-6 h-6 mx-auto text-muted-foreground/40" />
            <p>No coordinates on file yet for the places in this journey.</p>
            <p className="text-[11px]">Open Database &rarr; Locations and paste a Google Maps URL on each place to pin it.</p>
          </div>
        ) : null}

        {/* Tab content */}
        <div data-testid={`journey-content-${tab}`}>
          {tab === 'tours'  && <ToursList     tours={profile.tours || []}                 navigate={navigate} statusColors={statusColors} customerId={profile.id} onRefresh={onReload} />}
          {tab === 'hotels' && <HotelsList    hotels={profile.hotels_stayed || []}        navigate={navigate} />}
          {tab === 'places' && <PlacesList    places={profile.locations_visited || []}                                        navigate={navigate} />}
          {tab === 'people' && <PeopleList    people={profile.people_met || []}           navigate={navigate} />}
        </div>
      </CardContent>
      <AddTourFromInvoicesDialog
        open={addTourOpen}
        onClose={() => setAddTourOpen(false)}
        customer={profile}
        onCreated={() => onReload?.()}
      />
    </Card>
  );
};

const EmptyState = ({ icon: Icon, text }) => (
  <div className="text-center py-10 text-sm text-muted-foreground">
    {Icon && <Icon className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />}
    {text}
  </div>
);

const TripChip = ({ trip, onClick }) => (
  <button type="button" onClick={onClick}
    className="text-[10px] px-2 py-1 rounded-full bg-white border hover:bg-primary/5 hover:border-primary/30 text-muted-foreground whitespace-nowrap transition-colors">
    {trip.destination || trip.tour_name || 'Trip'}
    {trip.start_date ? ` · ${trip.start_date.slice(0, 7)}` : ''}
  </button>
);

const ToursList = ({ tours, navigate, statusColors, customerId, onRefresh }) => {
  const POLLEN_API = `${API}/pollen`;
  const authFetch = (url, opts = {}) => {
    const h = {}; const t = localStorage.getItem('session_token'); if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggle = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const doBulkDelete = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${POLLEN_API}/tours/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tour_ids: Array.from(selectedIds) }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Delete failed');
      const d = await res.json();
      toast.success(`Deleted ${d.count} tour${d.count === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      onRefresh?.();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const doMerge = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${POLLEN_API}/customers/${customerId}/tours/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tour_ids: Array.from(selectedIds) }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Merge failed');
      const d = await res.json();
      const detached = d.files_detached || 0;
      toast.success(
        `Merged into "${d.tour?.tour_name}"` +
        (detached ? ` · ${detached} file${detached === 1 ? '' : 's'} kept under customer` : '')
      );
      setSelectedIds(new Set());
      setMergeOpen(false);
      onRefresh?.();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  if (!tours.length) return <EmptyState icon={Compass} text="No tours yet — add one to start the journey." />;

  const selectedTours = tours.filter(t => selectedIds.has(t.id));
  const selectedSum = selectedTours.reduce((s, t) => s + Number(t.customer_amount || 0), 0);

  return (
    <div className="space-y-2">
      {tours.length > 1 && (
        <div className="flex items-center justify-between px-1 pb-1 text-[11px] text-muted-foreground">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-3.5 h-3.5 accent-primary cursor-pointer"
              checked={tours.length > 0 && tours.every(t => selectedIds.has(t.id))}
              onChange={() => setSelectedIds(prev => {
                if (tours.every(t => prev.has(t.id))) return new Set();
                return new Set(tours.map(t => t.id));
              })}
              data-testid="profile-tours-select-all"
            />
            Select all
          </label>
          {selectedIds.size > 0 && (
            <span data-testid="profile-tours-selected-sum">
              {selectedIds.size} selected · ₹{selectedSum.toLocaleString('en-IN')}
            </span>
          )}
        </div>
      )}
      {tours.map((t) => (
        <div key={t.id}
          className={`p-3 border rounded-lg cursor-pointer hover:bg-muted/40 transition-colors flex items-center gap-3 ${selectedIds.has(t.id) ? 'bg-primary/5 border-primary/30' : ''}`}
          onClick={(e) => { if (e.target.closest('[data-row-checkbox]')) return; navigate(`/pollen-dots/tour/${t.id}`); }}
          data-testid={`tour-history-${t.id}`}>
          <div data-row-checkbox className="shrink-0">
            <input
              type="checkbox"
              className="w-4 h-4 accent-primary cursor-pointer"
              checked={selectedIds.has(t.id)}
              onChange={(e) => { e.stopPropagation(); toggle(t.id); }}
              onClick={(e) => e.stopPropagation()}
              data-testid={`profile-tour-select-${t.id}`}
            />
          </div>
          <div className="space-y-1 min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm truncate">{t.tour_name}</span>
              <Badge className={statusColors[t.status] || 'bg-gray-100'} variant="outline">{t.status}</Badge>
              {t.is_merged && <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] px-1.5 py-0">Merged</Badge>}
              {t.is_historical && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0">Historical</Badge>}
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span><MapPin className="w-3 h-3 inline mr-1" />{t.destination || '-'}</span>
              <span><CalendarDays className="w-3 h-3 inline mr-1" />{formatDate(t.start_date)} → {formatDate(t.end_date)}</span>
              <span><Users className="w-3 h-3 inline mr-1" />{(t.adults || 0) + (t.children || 0)} pax</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="font-bold text-sm">₹{(t.customer_amount || 0).toLocaleString('en-IN')}</p>
            <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto" />
          </div>
        </div>
      ))}

      <BulkActionBar
        selectedCount={selectedIds.size}
        onClear={() => setSelectedIds(new Set())}
        onDelete={() => setBulkDeleteOpen(true)}
        onMerge={() => setMergeOpen(true)}
        merging={busy}
        deleting={busy}
        minMergeCount={2}
      />

      <ConfirmDeleteDialog
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={doBulkDelete}
        title={`Delete ${selectedIds.size} tour${selectedIds.size === 1 ? '' : 's'}?`}
        description="Tours will be deleted permanently. Any attached files will be kept under this customer's profile. This cannot be undone."
      />

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent data-testid="merge-tours-dialog">
          <DialogHeader>
            <DialogTitle>Merge {selectedIds.size} tours?</DialogTitle>
            <DialogDescription asChild>
              <div className="text-xs text-muted-foreground space-y-2 pt-2">
                <p>A new tour will be created for this customer that absorbs the selected ones:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Name lists every merged tour&apos;s invoice / tour name.</li>
                  <li>Oldest <span className="font-mono">invoice_date</span> wins (keeps correct FY bucket).</li>
                  <li><span className="font-mono">customer_amount</span> summed: <span className="font-semibold text-foreground">₹{selectedSum.toLocaleString('en-IN')}</span>.</li>
                  <li>Start/end dates, itinerary, hotels details, transport, guides, group — blanked.</li>
                  <li>Status &amp; Historical flag inherited from the oldest tour.</li>
                  <li>Attached files stay under this customer (not attached to any tour).</li>
                  <li>Source tours are <span className="font-semibold text-rose-600">permanently deleted</span>.</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-48 overflow-y-auto space-y-1 border rounded-md p-2 bg-muted/30">
            {selectedTours.map(t => (
              <div key={t.id} className="flex justify-between items-center text-xs gap-2">
                <span className="truncate">{t.tour_name}</span>
                <span className="text-muted-foreground shrink-0 tabular-nums">
                  {t.invoice_date ? t.invoice_date.slice(0, 10) : '—'} · ₹{(t.customer_amount || 0).toLocaleString('en-IN')}
                </span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)} disabled={busy} data-testid="merge-tours-cancel">Cancel</Button>
            <Button onClick={doMerge} disabled={busy} data-testid="merge-tours-confirm">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Merge {selectedIds.size} tours
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const HotelsList = ({ hotels, navigate }) => {
  if (!hotels.length) return <EmptyState icon={Bed} text="No hotels stayed at yet." />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {hotels.map((h, i) => (
        <div key={i} className="p-3 rounded-lg border bg-gradient-to-br from-white to-sky-50/30 hover:shadow-sm transition-shadow"
          data-testid={`journey-hotel-${i}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <Bed className="w-3.5 h-3.5 text-sky-600 shrink-0" />
                <p className="font-medium text-sm truncate">{h.hotel_name}</p>
              </div>
              {h.location && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{h.location}</p>}
            </div>
            <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200 shrink-0">
              <Repeat className="w-3 h-3 mr-0.5" />{h.trip_count}
            </Badge>
          </div>
          {h.trips?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {h.trips.slice(0, 3).map((tr, j) => (
                <TripChip key={j} trip={tr} onClick={() => navigate(`/pollen-dots/tour/${tr.tour_id}`)} />
              ))}
              {h.trips.length > 3 && <span className="text-[10px] text-muted-foreground self-center">+{h.trips.length - 3}</span>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const PlacesList = ({ places, navigate }) => {
  if (!places.length) return <EmptyState icon={MapPin} text="No places visited yet." />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="places-list">
      {places.map((l, i) => (
        <div key={i} className="p-3 rounded-lg border bg-gradient-to-br from-white to-teal-50/30 hover:shadow-sm hover:border-teal-300 transition-all"
          data-testid={`journey-place-${i}`}>
          <MapPin className="w-4 h-4 text-teal-600 mb-1" />
          <p className="text-sm font-medium truncate">{l.name}</p>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">{l.trip_count} trip{l.trip_count > 1 ? 's' : ''}</span>
            {l.trips?.[0] && (
              <button onClick={() => navigate(`/pollen-dots/tour/${l.trips[0].tour_id}`)}
                className="text-[10px] text-teal-600 hover:underline">view</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Lightweight Leaflet map — delegates to the shared PollenDotsMap component so
// every map in the app uses identical positioning, sizing, and pulse behaviour.
const JourneyMap = ({ pins, onPinClick }) => (
  <PollenDotsMap pins={pins} height={420} zoom={5} onPinClick={onPinClick} />
);

const PeopleList = ({ people, navigate }) => {
  if (!people.length) {
    return <EmptyState icon={UserIcon} text="No guides or drivers on record yet. Add them in the tour details — they'll appear here so your customer can remember the people who made their trip special." />;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {people.map((p, i) => (
        <PersonCard key={i} person={p} navigate={navigate} index={i} />
      ))}
    </div>
  );
};

const PersonCard = ({ person: p, navigate, index }) => {
  const isGuide = p.role === 'Guide';
  const roleColor = isGuide
    ? 'bg-rose-50 text-rose-700 border-rose-200'
    : 'bg-indigo-50 text-indigo-700 border-indigo-200';
  const initials = (p.name || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="p-4 rounded-xl border bg-gradient-to-br from-white to-rose-50/20 hover:shadow-md transition-shadow"
      data-testid={`journey-person-${index}`}>
      <div className="flex items-start gap-3">
        {/* Avatar — photo if available, else initials bubble */}
        {p.photo_url ? (
          <img src={p.photo_url} alt={p.name}
            className="w-14 h-14 rounded-full object-cover border-2 border-rose-100 shrink-0"
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-rose-100 to-pink-100 flex items-center justify-center text-rose-700 font-semibold text-sm border-2 border-rose-200 shrink-0">
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm truncate">{p.name}</p>
            <Badge variant="outline" className={`text-[10px] ${roleColor}`}>{p.role}</Badge>
          </div>
          {p.location && <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location}</p>}
          {p.phone && <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</p>}
        </div>
      </div>
      {p.bio && <p className="text-xs text-foreground/80 italic mt-2 line-clamp-3">"{p.bio}"</p>}
      <div className="mt-3 pt-2 border-t border-rose-100/80 flex flex-wrap items-center gap-1.5">
        <Badge variant="outline" className="text-[10px] bg-white"><Repeat className="w-3 h-3 mr-0.5" />{p.trip_count} trip{p.trip_count > 1 ? 's' : ''}</Badge>
        {p.trips?.slice(0, 2).map((tr, j) => (
          <TripChip key={j} trip={tr} onClick={() => navigate(`/pollen-dots/tour/${tr.tour_id}`)} />
        ))}
        {p.vendor_id && (
          <button onClick={() => navigate(`/pollen-dots/vendor/${p.vendor_id}`)}
            className="text-[10px] text-rose-600 hover:underline ml-auto">Profile →</button>
        )}
      </div>
    </div>
  );
};

export default PollenDotsCustomerProfile;
